/**
 * BodyShopGrossBreakdown
 *
 * Two side-by-side cards split the Body Shop blue tile total into Labor (45x/65x)
 * vs Parts (47x/67x) gross for the current month-to-date. Only renders when the
 * backend supplies bodyShopGrossByCategory (locations 1, 12, 44).
 */

import React from 'react';

export interface BodyShopGrossByCategory {
  labor: number;
  parts: number;
}

interface BodyShopGrossBreakdownProps {
  bodyShopGrossByCategory: BodyShopGrossByCategory | null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export const BodyShopGrossBreakdown: React.FC<BodyShopGrossBreakdownProps> = ({
  bodyShopGrossByCategory,
}) => {
  if (!bodyShopGrossByCategory) {
    return null;
  }

  const { labor, parts } = bodyShopGrossByCategory;
  const total = labor + parts;
  const laborPct = total !== 0 ? Math.round((labor / total) * 100) : 0;
  const partsPct = total !== 0 ? 100 - laborPct : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
          Body Labor Gross
        </div>
        <div className="text-2xl font-bold text-blue-900 mt-1">{formatCurrency(labor)}</div>
        <div className="text-xs text-blue-700 mt-1">
          {laborPct}% of Body Shop total
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
          Body Parts Gross
        </div>
        <div className="text-2xl font-bold text-blue-900 mt-1">{formatCurrency(parts)}</div>
        <div className="text-xs text-blue-700 mt-1">
          {partsPct}% of Body Shop total
        </div>
      </div>
    </div>
  );
};
